import { Sphere } from '@react-three/drei'
import { forwardRef, Suspense, useCallback, useImperativeHandle, useRef } from 'react'
import { Group, MeshBasicMaterial, Vector2Like } from 'three'
import { useGetDoorProductPositionOffset } from '~/components/DrawingCanvas/Doors/hooks'
import { useProject } from '~/components/DrawingCanvas/hooks'
import { LoadingIndicator } from '~/components/DrawingCanvas/Products/components/LoadingIndicator'
import { useDetailedLabel } from '~/components/DrawingCanvas/Products/hooks/useDetailedLabel'
import { useProductMesh } from '~/components/DrawingCanvas/Products/hooks/useProductMesh'
import { ToolManagerRef } from '~/components/DrawingCanvas/ToolManager'
import { isLeftMouseButton } from '~/components/DrawingCanvas/util/eventHandlers'
import { modelToUI, vectorModelToUI } from '~/components/DrawingCanvas/util/units'
import { DoorProduct } from '~/config/doorProducts'
import { showAlert } from '~/store/alert'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { updateObjects } from '~/store/objects'
import { Door } from '~/store/objects/types'
import { setActiveTool } from '~/store/tools'

const DISTANCE_THRESHOLD = 150
const DEFAULT_HEIGHT = modelToUI(120)

const DoorProductMesh = ({ name }: { name: string }) => {
  const mesh = useProductMesh(name)
  return <primitive object={mesh} />
}

const useGetClosestDoor = () => {
  const doors = useAppSelector(state => state.objects.present.doors)
  return useCallback(
    (point: Vector2Like) => {
      return Object.values(doors).find(
        ({ position }) => vectorModelToUI(position).distanceToSquared({ ...point, z: 0 }) < DISTANCE_THRESHOLD
      )
    },
    [doors]
  )
}

export const DoorProductTool = forwardRef<ToolManagerRef, { activeToolProps: DoorProduct }>(function DoorProductTool(
  { activeToolProps },
  ref
) {
  const dispatch = useAppDispatch()
  const getClosestDoor = useGetClosestDoor()
  const getPositionOffset = useGetDoorProductPositionOffset()
  const label = useDetailedLabel()
  const project = useProject()
  const group = useRef<Group>(null!)
  const guideMaterial = useRef<MeshBasicMaterial>(null!)

  useImperativeHandle(
    ref,
    () => ({
      onMouseDown() {
        return true
      },
      onMouseMove(event) {
        const point = project(event)
        if (!point) return false
        const closestDoor = getClosestDoor(point)
        if (closestDoor) {
          const { rotation, position } = closestDoor
          group.current.position.copy(vectorModelToUI(position))
          const { _x, _y, _z } = rotation
          group.current.rotation.set(_x, _y, _z)
          const offset = getPositionOffset(closestDoor, activeToolProps.orientations[0])
          group.current.position.add(offset)
          guideMaterial.current.color.set('green')
        } else {
          const { x, y } = point
          group.current.position.set(x, y, DEFAULT_HEIGHT)
          group.current.rotation.set(0, 0, 0)
          guideMaterial.current.color.set('red')
        }
        return true
      },
      onMouseUp(event) {
        if (!isLeftMouseButton) return false
        const point = project(event)
        if (!point) return true
        const closestDoor = getClosestDoor(point)
        if (!closestDoor) return true
        if (closestDoor.product) {
          dispatch(showAlert({ type: 'error', text: 'Door already has a product!' }))
          return true
        }
        const { name, sizes, category, orientations } = activeToolProps
        const defaultSize = sizes[0]
        const updatedDoor: Door = {
          ...closestDoor,
          product: {
            name,
            voltage: defaultSize.voltages[0],
            orientation: orientations[0],
            label,
            category,
            size: defaultSize.label,
          },
        }
        dispatch(updateObjects([updatedDoor]))
        dispatch(setActiveTool({ tool: 'SELECT_TOOL' }))
        return true
      },
    }),
    [project, getClosestDoor, getPositionOffset, activeToolProps, label]
  )
  return (
    <group ref={group}>
      <Sphere args={[5]}>
        <meshBasicMaterial ref={guideMaterial} transparent opacity={0.5} depthWrite={false} />
      </Sphere>
      <Suspense fallback={<LoadingIndicator />}>
        <DoorProductMesh name={activeToolProps.name} />
      </Suspense>
    </group>
  )
})
